/**
 * heroImages — curated home-studio + creator-desk imagery.
 *
 * Every image is:
 *   - Home / desk / cosy podcast setup (NOT large recording studios)
 *   - Warm daylight, wood + fabric textures, one mic vibe
 *   - Unsplash Free License (commercial use allowed)
 *
 * Each entry has descriptive alt text (SEO + a11y) and credit metadata
 * so the site can emit ImageObject JSON-LD.
 *
 * Prefer these over ad-hoc Unsplash URLs. If a page needs a new photo,
 * add it here so every image on the site stays on-brand.
 */

export const HERO = {
  // Home podcast desk — warm wood, small mic, laptop, creator working calmly.
  homePodcastDesk: {
    src: 'https://images.unsplash.com/photo-1590602847861-f357a9332bbc?w=1800&q=80&auto=format&fit=crop',
    alt: 'Creator recording a podcast at a warm home desk with a microphone and laptop, Audio & Co. podcast management',
    title: 'Podcast Management for Coaches | Audio & Co.',
    credit: 'Photo by Soundtrap on Unsplash',
    creator: 'Soundtrap',
    width: 1800,
    height: 1200,
  },
  // Cosy laptop + notebook on a wooden desk near a window.
  cosyDeskNotebook: {
    src: 'https://images.unsplash.com/photo-1483058712412-4245e9b90334?w=1800&q=80&auto=format&fit=crop',
    alt: 'Home workspace with a laptop and open notebook on a wooden desk, planning an SEO business website with Audio & Co.',
    title: 'SEO Business Websites | Audio & Co.',
    credit: 'Photo by Carl Heyerdahl on Unsplash',
    creator: 'Carl Heyerdahl',
    width: 1800,
    height: 1200,
  },
  // Warm home studio — creator in soft daylight, one condenser mic, houseplant.
  warmHomeStudio: {
    src: 'https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=1800&q=80&auto=format&fit=crop',
    alt: 'Solo host recording a podcast at home with a condenser microphone in soft daylight, done-for-you podcast production',
    title: 'Done-for-You Podcast Production | Audio & Co.',
    credit: 'Photo by Will Francis on Unsplash',
    creator: 'Will Francis',
    width: 1800,
    height: 1200,
  },
  // Editing headphones + laptop close-up — for editing/production service pages.
  editingCloseUp: {
    src: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=1800&q=80&auto=format&fit=crop',
    alt: 'Podcast editor working on audio waveforms on a laptop with over-ear headphones, Audio & Co. podcast editing',
    title: 'Podcast Editing Service | Audio & Co.',
    credit: 'Photo by James Owen on Unsplash',
    creator: 'James Owen',
    width: 1800,
    height: 1200,
  },
  // Home mic + phone recording — for launch / DIY setups.
  launchSetup: {
    src: 'https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=1800&q=80&auto=format&fit=crop',
    alt: 'Home podcast setup with a microphone, headphones and warm lighting, ready to launch a new podcast with Audio & Co.',
    title: 'Podcast Launch Service | Audio & Co.',
    credit: 'Photo by Jonathan Velasquez on Unsplash',
    creator: 'Jonathan Velasquez',
    width: 1800,
    height: 1200,
  },
  // Warm home YouTube setup — camera on desk, host visible in soft light.
  cameraOnDesk: {
    src: 'https://images.unsplash.com/photo-1601506521937-0121a7fc2a6b?w=1800&q=80&auto=format&fit=crop',
    alt: 'Home YouTube recording setup with a camera on a wooden desk in daylight, for YouTube management by Audio & Co.',
    title: 'YouTube Channel Management | Audio & Co.',
    credit: 'Photo by ConvertKit on Unsplash',
    creator: 'ConvertKit',
    width: 1800,
    height: 1200,
  },
  // Author writing at home — for self publishing / audiobook pages.
  authorWriting: {
    src: 'https://images.unsplash.com/photo-1519682337058-a94d519337bc?w=1800&q=80&auto=format&fit=crop',
    alt: 'Author writing in a hardback notebook beside a stack of books, preparing to self-publish with Audio & Co.',
    title: 'Self-Publishing Services UK | Audio & Co.',
    credit: 'Photo by Aaron Burden on Unsplash',
    creator: 'Aaron Burden',
    width: 1800,
    height: 1200,
  },
  // Audiobook narration — mic + script.
  audiobookMic: {
    src: 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=1800&q=80&auto=format&fit=crop',
    alt: 'Home studio microphone with a printed script ready for audiobook narration, Audio & Co. audiobook production',
    title: 'Audiobook Production UK | Audio & Co.',
    credit: 'Photo by Jonathan Velasquez on Unsplash',
    creator: 'Jonathan Velasquez',
    width: 1800,
    height: 1200,
  },
  // Warm creator laptop — for blog + results pages.
  creatorLaptop: {
    src: 'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=1800&q=80&auto=format&fit=crop',
    alt: 'Creator working on a laptop at a bright desk by a window, with content handled by Audio & Co. podcast management',
    title: 'Done-for-You Content | Audio & Co.',
    credit: 'Photo by Andrew Neel on Unsplash',
    creator: 'Andrew Neel',
    width: 1800,
    height: 1200,
  },
  // Home office with plants + laptop — for websites/apps + about pages.
  homeOfficePlants: {
    src: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1800&q=80&auto=format&fit=crop',
    alt: 'Cosy home office with a laptop, houseplants and natural light, time won back with Audio & Co. podcast management',
    title: 'Podcast Management Saves Time | Audio & Co.',
    credit: 'Photo by Helena Lopes on Unsplash',
    creator: 'Helena Lopes',
    width: 1800,
    height: 1200,
  },
  // SEO / content strategy visual — coffee, laptop, notes.
  contentStrategy: {
    src: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1800&q=80&auto=format&fit=crop',
    alt: 'Content strategy session with a laptop, notebook and coffee on a desk, planning SEO for a business website',
    title: 'SEO Content Strategy | Audio & Co.',
    credit: 'Photo by Kaleidico on Unsplash',
    creator: 'Kaleidico',
    width: 1800,
    height: 1200,
  },
};

export default HERO;
